import { useEffect } from "react";
import { Navigate, NavLink, Outlet, useNavigate, useOutletContext } from "react-router";

import { checkTabIsActive, IdValue, Spinner } from "@prequel/shared-components";

import type { ProviderResource } from "@/api/import/providers";
import { useProvider } from "@/api/import/providers";
import DetailPageLayout from "@/components/import/DetailPageLayout";
import { useRequiredParam } from "@/layouts/Navigation/useRequiredParam";
import { useToast } from "@/providers/ToastProvider";

export type ProviderContext = {
  provider: ProviderResource;
};

export function useProviderContext(): ProviderContext {
  return useOutletContext<ProviderContext>();
}

const ProviderPage = () => {
  const providerID = useRequiredParam("providerID");
  const { addToast } = useToast();
  const { data: provider, error, isLoading } = useProvider(providerID);

  useEffect(() => {
    if (error) {
      addToast({ kind: "ERROR", message: "Failed to load provider." });
    }
  }, [error, addToast]);

  if (isLoading) {
    return <Spinner />;
  }

  if (!provider) {
    return <Navigate to="/import/providers" replace />;
  }

  return <Outlet context={{ provider }} />;
};

export const ProviderDetailsPage = () => {
  const navigate = useNavigate();
  const { provider } = useProviderContext();
  const basePath = `/import/providers/${provider.id}`;

  const tabs = [
    <NavLink key="Overview" to={basePath} className={checkTabIsActive} replace end>
      Overview
    </NavLink>,
    <NavLink key="Sources" to={`${basePath}/sources`} className={checkTabIsActive} replace end>
      Sources
    </NavLink>,
    <NavLink key="Admin" to={`${basePath}/admin`} className={checkTabIsActive} replace end>
      Admin
    </NavLink>,
  ];

  return (
    <DetailPageLayout
      title={provider.name}
      onGoBack={() => navigate("/import/providers")}
      subheading={
        <div className="flex items-center gap-2">
          <IdValue id={provider.id} />
        </div>
      }
      tabs={tabs}
    >
      <Outlet context={{ provider }} />
    </DetailPageLayout>
  );
};

export default ProviderPage;
